import React, { memo, ReactNode } from "react";
import { Box } from "@mui/material";
import BankExtractionAmbiguousField from "@investorRequest/components/BankExtractionAmbiguousField";
import BankExtractionEditedBadge from "@investorRequest/components/BankExtractionEditedBadge";
import {
  BankExtractionFieldChrome,
  BankExtractionFieldDescription,
} from "@investorRequest/components/BankExtractionFieldChrome";
import { useBankExtractionForm } from "@investorRequest/components/BankExtractionFormContext";
import { bankExtractionCopy } from "@investorRequest/constants/bankExtractionCopy";

interface Props {
  fieldName: string;
  label?: string;
  children: ReactNode;
  value: string;
  onChange: (value: string) => void;
}

const ExtractionFieldWrapper = ({ fieldName, label, children, value, onChange }: Props) => {
  const { isActive, getFieldMeta, onFieldChange, onAmbiguousAcknowledge } = useBankExtractionForm();
  const descriptionId = `extraction-desc-${fieldName}`;
  const meta = getFieldMeta(fieldName);

  if (!isActive || !meta) {
    return <>{children}</>;
  }

  if (meta.isAmbiguous && meta.candidates) {
    return (
      <Box>
        <BankExtractionAmbiguousField
          fieldName={fieldName}
          value={value}
          candidates={meta.candidates}
          label={label}
          onChange={(next) => {
            onChange(next);
            onFieldChange(fieldName, next);
          }}
          onAcknowledge={() => onAmbiguousAcknowledge(fieldName)}
          onCustomValue={(next) => onFieldChange(fieldName, next)}
        />
        {meta.showEditedBadge ? <BankExtractionEditedBadge /> : null}
      </Box>
    );
  }

  return (
    <Box position="relative">
      <Box display="flex" alignItems="center" gap={1}>
        <Box flex={1}>{children}</Box>
        <BankExtractionFieldChrome confidence={meta.confidence} describedById={descriptionId} />
        {meta.showEditedBadge ? <BankExtractionEditedBadge /> : null}
      </Box>
      <BankExtractionFieldDescription id={descriptionId} confidence={meta.confidence} />
      {meta.confidence === "missing" && !value ? (
        <Box component="span" color="error.main" fontSize={12} mt={0.5} display="block">
          {bankExtractionCopy.missingFieldHelper}
        </Box>
      ) : null}
    </Box>
  );
};

export default memo(ExtractionFieldWrapper);
